import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Box, Typography, Button, Tab, Tabs, Avatar, TextField, Grid2, IconButton, Stack, Divider } from '@mui/material';
import { useAuth } from '../../services/authentication/authContext';
import { jwtDecode } from 'jwt-decode';
import { fetchUserDetails } from '../redux/userDetailsSlice';
import { useDispatch, useSelector } from 'react-redux';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { userServices } from '../../services/userServices';
import PageHeader from '../ui/PageHeader';
import '../../css/Profile.css';

const Profile = () => {

  const { accessToken } = useAuth();
  const dispatch = useDispatch();
  const { data: userData, loading, error } = useSelector(state => state.userDetails);

  const [tabIndex, setTabIndex] = useState(0);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');

  useEffect(() => {
    if (accessToken && !userData.length) {
      const decodedToken = jwtDecode(accessToken);
      const userEmail = decodedToken.sub;
      dispatch(fetchUserDetails(userEmail));
    }
  }, [accessToken, dispatch, userData]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const validatePasswordRules = (password) => {
    const hasCapital = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!password) return ''; // Don't show error if empty (handled by required check)
    if (!hasCapital) return "Password must contain at least one capital letter.";
    if (!hasNumber) return "Password must contain at least one number.";
    if (!hasSpecial) return "Password must contain at least one special character.";
    return "";
  };

  const validatePasswords = () => {
    let valid = true;

    if (currentPassword === '') {
      setCurrentPasswordError('Current password is required');
      valid = false;
    } else {
      setCurrentPasswordError('');
    }

    if (newPassword === '') {
      setNewPasswordError('New password is required');
      valid = false;
    } else if (currentPassword === newPassword) {
      setNewPasswordError('New password cannot be the same as current password');
      valid = false;
    } else {
      setNewPasswordError('');
    }

    return valid;
  };

  const confirmUpdatePassword = () => {
    if (validatePasswords()) {
      const type = "update";

      const formData = {
        id: userData.id,
        type,
        newPassword,
        currentPassword,
      };

      if ((currentPassword && newPassword) !== null) {
        userServices.resetPasswordById(formData)
          .then(() => {
            setSnackbarMessage('Password updated successfully!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            setTimeout(() => {
              setSnackbarMessage(null);
              setOpenSnackbar(false);
            }, 4000);
            setCurrentPassword('');
            setNewPassword('');
          })
          .catch((error) => {
            setSnackbarMessage('Please enter correct existing password!');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            setTimeout(() => {
              setSnackbarMessage(null);
              setOpenSnackbar(false);
            }, 4000);
          });
      }
    }
  };

  const handleCurrentPasswordChange = (e) => {
    setCurrentPassword(e.target.value);
    if (e.target.value !== '') {
      setCurrentPasswordError('');
    }
  };

  const handleNewPasswordChange = (e) => {

    const value = e.target.value;
    setNewPassword(value);

    if (value === '') {
      setNewPasswordError('New password is required');
    } else if (currentPassword === value) {
      setNewPasswordError('New password cannot be the same as current password');
    } else {
      // Check rules in real-time
      const ruleError = validatePasswordRules(value);
      setNewPasswordError(ruleError); // Will be empty string if valid
    }
  };

  const handleToggleCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
  const handleToggleNewPassword = () => setShowNewPassword(!showNewPassword);

  const renderUserInfo = () => (
    <Box className="profile-details-section">
      <Stack spacing={0}>
        <DetailItem label="Email Address" value={userData.emailId} />
        <DetailItem label="Mobile Number" value={userData.mobileNo} />
        <DetailItem label="Official Address" value={userData.address} />
        <DetailItem label="Birth Date" value={userData.birthDate} />
      </Stack>
    </Box>
  );

  const DetailItem = ({ label, value }) => (
    <Box className="profile-detail-item">
      <Typography className="profile-detail-label">{label}</Typography>
      <Typography className="profile-detail-value">{value || 'N/A'}</Typography>
    </Box>
  );

  const renderPasswordUpdate = () => (
    <Box className="password-section">
      <Box className="password-form-wrapper">
        <Typography variant="h6" className="password-section-header">Update Password</Typography>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              label="Current Password"
              variant="outlined"
              fullWidth
              value={currentPassword}
              onChange={handleCurrentPasswordChange}
              type={showCurrentPassword ? 'text' : 'password'}
              required
              error={currentPasswordError !== ''}
              helperText={currentPasswordError || ''}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton onClick={handleToggleCurrentPassword} edge="end">
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                },
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              label="New Password"
              variant="outlined"
              fullWidth
              value={newPassword}
              onChange={handleNewPasswordChange}
              type={showNewPassword ? 'text' : 'password'}
              required
              error={newPasswordError !== ''}
              helperText={newPasswordError || ' '}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton onClick={handleToggleNewPassword} edge="end">
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                },
              }}
            />
          </Grid2>
        </Grid2>
        <Button
          fullWidth
          className="profile-action-btn"
          variant="contained"
          onClick={confirmUpdatePassword}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box className="profile-page-container">
      <PageHeader title="Profile" />

      {loading ? (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#64748b' }}>Loading profile...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="body1" color="error">{error}</Typography>
        </Box>
      ) : userData ? (
        <>
          <Snackbar
            open={openSnackbar}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          <Box className="profile-tabs-wrapper">
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              className="profile-tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="User Info" className="profile-tab-item" />
              <Tab label="Update Password" className="profile-tab-item" />
            </Tabs>
          </Box>

          <Box className="profile-card">
            <Box className="profile-avatar-section">
              <Avatar
                className="profile-avatar"
                src={`https://ui-avatars.com/api/?name=${userData.firstName}+${userData.lastName}&background=6366f1&color=fff&size=200`}
              />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" className="profile-user-name">
                  {userData.firstName} {userData.lastName}
                </Typography>
                <Typography className="profile-user-designation">
                  {userData.designation}
                </Typography>
              </Box>
            </Box>

            <Divider className="profile-divider" />

            {tabIndex === 0 && renderUserInfo()}
            {tabIndex === 1 && renderPasswordUpdate()}
          </Box>
        </>
      ) : (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="body1">No user data found.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Profile;
